import React from 'react';
import { TrendingUp, Zap, ArrowRight } from 'lucide-react';
import { track } from '../utils/tracker';
import { ReactComponent as PersonalAccountIcon } from '../exchange-app-icons/personal-account.svg';
import { ReactComponent as VerifiedCheckIcon } from '../exchange-app-icons/verified-check.svg';
import { ReactComponent as SealWarningIcon } from '../exchange-app-icons/seal-warning-fill.svg';
import { ReactComponent as QuestionFillIcon } from '../exchange-app-icons/question-fill.svg';
import { ReactComponent as AlipayIcon } from '../exchange-app-icons/alipay-12.svg';
import { ReactComponent as WeChatIcon } from '../exchange-app-icons/wechat-7.svg';

const MainMenu = ({ setCurrentScreen, setProfileActiveSection, exchangeRate, usdtToCnyRate, userFirstName, userPhotoUrl, verificationStatus }) => {
  const isAdmin = verificationStatus?.isAdmin || false;
  const isVerified = verificationStatus?.isVerified || isAdmin || false;
  const isVerificationLoading = verificationStatus?.isLoading || false;
  
  return (
  <div className="min-h-screen bg-gradient-to-b from-gray-900 via-gray-900 to-black p-4">
    <div className="max-w-md mx-auto pt-16">
      
      {/* Header Section */}
      <div className="text-center mb-6">
        <img
          src="/logo.png"
          alt="Kit8 Exchange"
          className="w-24 h-auto mx-auto mb-3"
          loading="eager"
          fetchpriority="high"
        />
        <h1 className="text-2xl font-bold text-white mb-1">Kit8 Exchange</h1>
        <p className="text-gray-500 text-sm">Пополнение Alipay и WeChat</p>
      </div>

      {/* User Greeting */}
      <div className="bg-gray-800/50 rounded-2xl p-4 mb-4 border border-gray-700/50">
        <div className="flex items-center gap-3">
          <div className="relative">
            {userPhotoUrl ? (
              <img
                src={userPhotoUrl}
                alt="Profile"
                className="w-12 h-12 rounded-full border-2 border-purple-500/30 object-cover"
              />
            ) : (
              <div className="w-12 h-12 rounded-full bg-purple-500/20 flex items-center justify-center border-2 border-purple-500/30">
                <PersonalAccountIcon className="w-6 h-6" style={{ fill: '#c084fc' }} />
              </div>
            )}
            {/* Verification/Admin badge */}
            {!isVerificationLoading && (
              <div className={`absolute -bottom-1 -right-1 rounded-full p-0.5 border-2 border-gray-900 ${
                isAdmin ? 'bg-purple-500' : isVerified ? 'bg-green-500' : 'bg-amber-500'
              }`}>
                {isAdmin ? (
                  <span className="w-3 h-3 text-white flex items-center justify-center text-xs">👑</span>
                ) : isVerified ? (
                  <VerifiedCheckIcon className="w-3 h-3" style={{ fill: 'white' }} />
                ) : (
                  <SealWarningIcon className="w-3 h-3" style={{ fill: 'white' }} />
                )}
              </div>
            )}
          </div>
          <div className="flex-1">
            <p className="text-gray-400 text-sm">Добро пожаловать,</p>
            <div className="flex items-center gap-2">
              <p className="text-white font-semibold text-lg">
                {userFirstName || 'Гость'}
              </p>
              {isAdmin && (
                <span className="px-2 py-0.5 rounded-full bg-purple-500/20 border border-purple-500/50 text-purple-400 text-xs font-medium">
                  Admin
                </span>
              )}
            </div>
          </div>
          {/* Verification status - show only for non-admins */}
          {!isVerificationLoading && !isVerified && !isAdmin && (
            <button
              onClick={() => {
                setProfileActiveSection('kyc');
                setCurrentScreen('profile');
              }}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-500/20 border border-emerald-500/50 text-emerald-400 text-xs font-medium hover:bg-emerald-500/30 transition-all"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" viewBox="0 0 256 256"><path d="M208,40H48A16,16,0,0,0,32,56v56c0,52.72,25.52,84.67,46.93,102.19,23.06,18.86,46,25.27,47,25.53a8,8,0,0,0,4.2,0c1-.26,23.91-6.67,47-25.53C198.48,196.67,224,164.72,224,112V56A16,16,0,0,0,208,40Zm-48,96H136v24a8,8,0,0,1-16,0V136H96a8,8,0,0,1,0-16h24V96a8,8,0,0,1,16,0v24h24a8,8,0,0,1,0,16Z"></path></svg>
              Остался один шаг
            </button>
          )}
        </div>
      </div>

      {/* Exchange Rates */}
      <div className="grid grid-cols-2 gap-3 mb-6">
        <div className="bg-gray-800/50 rounded-2xl p-4 border border-gray-700/50">
          <div className="flex items-center gap-2 mb-2">
            <TrendingUp className="w-4 h-4 text-blue-400" />
            <span className="text-xs text-gray-500">RUB → CNY</span>
          </div>
          <p className="text-xl font-bold text-white">
            {exchangeRate ? exchangeRate : '—'}
            <span className="text-sm text-gray-400 ml-1">₽/¥</span>
          </p>
        </div>

        <div className="bg-gray-800/50 rounded-2xl p-4 border border-gray-700/50">
          <div className="flex items-center gap-2 mb-2">
            <Zap className="w-4 h-4 text-green-400" />
            <span className="text-xs text-gray-500">USDT → CNY</span>
          </div>
          <p className="text-xl font-bold text-white">
            {usdtToCnyRate ? usdtToCnyRate : '—'}
            <span className="text-sm text-gray-400 ml-1">$/¥</span>
          </p>
        </div>
      </div>

      {/* Main Actions */}
      <div className="space-y-3 mb-6">
        {/* Profile Button */}
        <button
          onClick={() => setCurrentScreen('profile')}
          className="w-full bg-gray-800 rounded-2xl py-4 px-5 flex items-center justify-between border border-gray-700 hover:bg-gray-750 hover:border-purple-500/50 transition-all"
        >
          <div className="flex items-center gap-4">
            <div className="bg-purple-500/20 rounded-xl p-2.5">
              <PersonalAccountIcon className="w-5 h-5" style={{ fill: '#c084fc' }} />
            </div>
            <div className="text-left">
              <p className="font-semibold text-white">Личный кабинет</p>
              <p className="text-gray-500 text-xs">Заявки и история</p>
            </div>
          </div>
          <ArrowRight className="w-5 h-5 text-gray-500" />
        </button>

        {/* Alipay Button */}
        <button
          onClick={() => setCurrentScreen('deposit')}
          className="w-full bg-gray-800 rounded-2xl py-4 px-5 flex items-center justify-between border border-gray-700 hover:bg-gray-750 hover:border-blue-500/50 transition-all"
        >
          <div className="flex items-center gap-4">
            <div className="bg-blue-500/20 rounded-xl p-2.5">
              <AlipayIcon className="w-6 h-6" style={{ fill: '#02A9F1' }} />
            </div>
            <div className="text-left">
              <p className="font-semibold text-white">Alipay</p>
              <p className="text-gray-500 text-xs">Пополнить кошелёк</p>
            </div>
          </div>
          <ArrowRight className="w-5 h-5 text-gray-500" />
        </button>

        {/* WeChat Button */}
        <button
          onClick={() => setCurrentScreen('wechat')}
          className="w-full bg-gray-800 rounded-2xl py-4 px-5 flex items-center justify-between border border-gray-700 hover:bg-gray-750 hover:border-green-500/50 transition-all"
        >
          <div className="flex items-center gap-4">
            <div className="bg-green-500/20 rounded-xl p-2.5">
              <WeChatIcon className="w-6 h-6" style={{ fill: '#51C332' }} />
            </div>
            <div className="text-left">
              <p className="font-semibold text-white">WeChat Pay</p>
              <p className="text-gray-500 text-xs">Пополнить кошелёк</p>
            </div>
          </div>
          <ArrowRight className="w-5 h-5 text-gray-500" />
        </button>
      </div>

      {/* Help Button */}
      <button
        onClick={() => setCurrentScreen('support')}
        className="w-full bg-gray-800/50 rounded-2xl py-3 px-5 flex items-center justify-center gap-2 border border-gray-700/50 hover:bg-gray-800 transition-all"
      >
        <QuestionFillIcon className="w-4 h-4" style={{ fill: '#6b7280' }} />
        <span className="text-gray-400 text-sm">Помощь</span>
      </button>

      {/* Footer */}
      <p className="text-center text-xs text-gray-600 mt-8">Безопасные переводы 24/7</p>
    </div>
  </div>
  );
};

export default MainMenu;
